import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import styles from './index.module.scss'
import useDebouncedSuggestions from './useDebouncedSuggestions'

export default function SearchSuggestInput ({
  id,
  name,
  value,
  onChange,
  onSelect,
  loadSuggestions,
  placeholder = '',
  disabled = false,
  maxLength,
  className,
  ariaInvalid = false,
  ariaDescribedBy
}) {
  const [query, setQuery] = useState('')
  const { items, setItems, open, setOpen, highlight, setHighlight } = useDebouncedSuggestions(loadSuggestions, query)
  const containerRef = useRef(null)
  const emittedValueRef = useRef(value || '')

  useEffect(() => {
    const nextValue = value || ''
    if (nextValue === emittedValueRef.current) return undefined

    emittedValueRef.current = nextValue
    setQuery('')
    setItems([])
    setOpen(false)
    setHighlight(-1)
    return undefined
  }, [value, setItems, setOpen, setHighlight])

  useEffect(() => {
    function handlePointerDown (event) {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [setOpen])

  function choose (item) {
    const nextValue = String(item.label || item.value || '')
    emittedValueRef.current = nextValue
    setQuery('')
    setItems([])
    setOpen(false)
    setHighlight(-1)
    onChange(nextValue)
    onSelect(item)
  }

  function handleChange (event) {
    const nextValue = event.target.value
    emittedValueRef.current = nextValue
    setQuery(nextValue)
    onChange(nextValue)
  }

  function handleKeyDown (event) {
    if (event.key === 'Escape') {
      setOpen(false)
      setHighlight(-1)
      return
    }

    if (!open || items.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlight((current) => (current + 1) % items.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlight((current) => (current <= 0 ? items.length - 1 : current - 1))
      return
    }

    if (event.key === 'Enter' && highlight >= 0) {
      event.preventDefault()
      choose(items[highlight])
    }
  }

  const listId = `${id}-list`

  return (
    <div className={styles.wrap} ref={containerRef}>
      <input
        id={id}
        name={name}
        type='text'
        className={className}
        value={value || ''}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        autoComplete='off'
        spellCheck={false}
        role='combobox'
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete='list'
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy || undefined}
        aria-activedescendant={highlight >= 0 ? `${id}-option-${highlight}` : undefined}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (items.length > 0) setOpen(true)
        }}
      />
      {open && (
        <ul id={listId} className={styles.list} role='listbox'>
          {items.map((item, index) => (
            <li
              key={`${item.id}-${index}`}
              id={`${id}-option-${index}`}
              role='option'
              aria-selected={index === highlight}
              className={cn(styles.option, { [styles.optionActive]: index === highlight })}
              onMouseEnter={() => setHighlight(index)}
              onMouseDown={(event) => {
                event.preventDefault()
                choose(item)
              }}>
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

SearchSuggestInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  loadSuggestions: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  className: PropTypes.string,
  ariaInvalid: PropTypes.bool,
  ariaDescribedBy: PropTypes.string
}
